import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, createVNode as _createVNode, withCtx as _withCtx, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "edit-waypoint-root" }
const _hoisted_2 = { class: "edit-waypoint-header" }
const _hoisted_3 = { class: "edit-waypoint-header__text" }
const _hoisted_4 = { class: "edit-waypoint-header__title-row" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = {
  key: 0,
  class: "edit-waypoint-header__subtitle"
}
const _hoisted_8 = { class: "edit-waypoint-body" }
const _hoisted_9 = {
  key: 0,
  class: "edit-waypoint-meta"
}
const _hoisted_10 = {
  class: "edit-waypoint-inspection",
  role: "status"
}
const _hoisted_11 = {
  key: 0,
  class: "edit-waypoint-inspection__list"
}
const _hoisted_12 = { class: "edit-waypoint-name-type" }
const _hoisted_13 = { class: "edit-waypoint-field edit-waypoint-field--name" }
const _hoisted_14 = { class: "edit-waypoint-field edit-waypoint-field--type" }
const _hoisted_15 = { class: "edit-waypoint-coords" }
const _hoisted_16 = { class: "edit-waypoint-coords__google" }
const _hoisted_17 = { class: "edit-waypoint-hint edit-waypoint-coords__hint" }
const _hoisted_18 = { class: "edit-waypoint-field edit-waypoint-field--notes" }
const _hoisted_19 = {
  key: 1,
  class: "edit-waypoint-message error",
  role: "alert"
}
const _hoisted_20 = { class: "edit-waypoint-footer" }

import { computed, nextTick, onUnmounted, reactive, ref, watch } from 'vue';
import { Button, Checkbox, Input, Select, TextArea } from '@/components/ui';
import type { SelectOption } from '@/components/ui';
import { defaultTippyConfig, tippy, type TippyInstance } from '@/modules/shipments/utils/tippy-init';
import {
  closeEditWaypointModal,
  getEditWaypointModalState,
  type EditWaypointModalSavePayload
} from './edit-waypoint-modal-controller';


export default /*@__PURE__*/_defineComponent({
  __name: 'EditWaypointModal',
  setup(__props) {

const typeOptions: SelectOption[] = [
  { value: 'airport', label: 'Airport' },
  { value: 'customs', label: 'Customs' },
  { value: 'destination', label: 'Destination' },
  { value: 'endpoint', label: 'Endpoint' },
  { value: 'facility', label: 'Facility' },
  { value: 'origin', label: 'Origin' },
  { value: 'third-party', label: 'Third-Party' },
  { value: 'city', label: 'City' },
  { value: 'state/province', label: 'State/Province' },
  { value: 'country', label: 'Country' }
];

const INSPECTION_REASON_LABELS: Record<string, string> = {
  geocode_failed: 'Geocoding failed',
  coordinates_missing: 'Coordinates missing',
  location_incomplete: 'Location incomplete',
  timezone_missing: 'Timezone missing',
  low_confidence_match: 'Low-confidence match'
};

const modalState = getEditWaypointModalState();
const open = computed(() => modalState.open);
const saving = computed(() => modalState.saving);
const errorMessage = computed(() => modalState.error);
const infoTooltipRef = ref<HTMLButtonElement | null>(null);
let infoTooltip: TippyInstance | null = null;

const meta = reactive({
  inspectionRequired: false,
  verified: false,
  inspectionReasons: [] as string[],
  dateCreated: null as unknown,
  dateModified: null as unknown,
  displayCity: '',
  displayCountry: ''
});

const form = reactive({
  type: '',
  coordinates: '',
  googleFormat: false,
  name: '',
  notes: ''
});

const inspectionReasonLabels = computed(() => (
  meta.inspectionReasons.map(reason => INSPECTION_REASON_LABELS[reason] || reason)
));

const showInspectionAlert = computed(() => (
  meta.inspectionRequired && !meta.verified
));

function formatWaypointDate(value: unknown): string {
  if (value == null || value === '') return '—';

  let date: Date | null = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'object') {
    const stamp = value as {
      toDate?: () => Date;
      seconds?: number;
      _seconds?: number;
    };
    if (typeof stamp.toDate === 'function') {
      try {
        date = stamp.toDate();
      } catch {
        date = null;
      }
    } else if (typeof stamp.seconds === 'number') {
      date = new Date(stamp.seconds * 1000);
    } else if (typeof stamp._seconds === 'number') {
      date = new Date(stamp._seconds * 1000);
    }
  } else if (typeof value === 'number' && Number.isFinite(value)) {
    date = new Date(value > 1e12 ? value : value * 1000);
  } else if (typeof value === 'string' && value.trim()) {
    const parsed = new Date(value);
    date = Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

const dateCreatedLabel = computed(() => formatWaypointDate(meta.dateCreated));
const dateModifiedLabel = computed(() => formatWaypointDate(meta.dateModified));

const infoTooltipContent = computed(() => [
  `Created on: ${dateCreatedLabel.value}`,
  `Modified on: ${dateModifiedLabel.value}`,
  `ID: ${modalState.waypointId || '—'}`
].join('<br>'));

const locationHeaderTitle = computed(() => {
  const parts = [meta.displayCity.trim(), meta.displayCountry.trim()].filter(Boolean);
  return parts.join(', ');
});

const coordinatesPlaceholder = computed(() => (
  form.googleFormat
    ? 'latitude, longitude (e.g., 43.6532, -79.3832)'
    : 'longitude, latitude (e.g., -79.3832, 43.6532)'
));

const coordinatesHelp = computed(() => (
  form.googleFormat
    ? 'Format: Latitude/Longitude'
    : 'Format: Longitude/Latitude'
));

function formatCountryDisplay(country?: unknown, countryCode?: unknown): string {
  const name = String(country || '').trim();
  const code = String(countryCode || '').trim().toUpperCase();
  if (!name && !code) return '';
  if (name && code && !name.toUpperCase().includes(`(${code})`)) {
    return `${name} (${code})`;
  }
  return name || code;
}

function destroyInfoTooltip(): void {
  infoTooltip?.destroy();
  infoTooltip = null;
}

async function initInfoTooltip(): Promise<void> {
  destroyInfoTooltip();
  await nextTick();
  if (!infoTooltipRef.value || !open.value) return;

  const instance = tippy(infoTooltipRef.value, {
    ...defaultTippyConfig,
    content: infoTooltipContent.value,
    allowHTML: true,
    placement: 'bottom'
  });
  infoTooltip = Array.isArray(instance) ? instance[0] ?? null : instance;
}

function pauseShipmentModalTooltips(): void {
  const shipmentModal = document.getElementById('modal');
  if (!shipmentModal) return;
  const selectors = [
    '.action-buttons.refresh',
    '.action-buttons.bookmark',
    '.action-buttons.arrow.previous',
    '.action-buttons.arrow.next'
  ];
  for (const selector of selectors) {
    const button = shipmentModal.querySelector(selector) as (HTMLElement & { _tippy?: { destroy: () => void } }) | null;
    button?._tippy?.destroy();
  }
}

function resumeShipmentModalTooltips(): void {
  const shipmentModal = document.getElementById('modal');
  const tip = (window as Window & {
    JidokaIconTooltip?: (el: Element, content: string) => void;
  }).JidokaIconTooltip;
  if (!shipmentModal || !tip) return;

  const refreshBtn = shipmentModal.querySelector('.action-buttons.refresh');
  const bookmarkBtn = shipmentModal.querySelector('.action-buttons.bookmark');
  const prevBtn = shipmentModal.querySelector('.action-buttons.arrow.previous');
  const nextBtn = shipmentModal.querySelector('.action-buttons.arrow.next');

  if (refreshBtn && !(refreshBtn as HTMLElement & { _tippy?: unknown })._tippy) {
    tip(refreshBtn, 'Refresh shipment <kbd>R</kbd>');
  }
  if (bookmarkBtn && !(bookmarkBtn as HTMLElement & { _tippy?: unknown })._tippy) {
    tip(bookmarkBtn, 'Toggle bookmark <kbd>B</kbd>');
  }
  if (prevBtn && !(prevBtn as HTMLElement & { _tippy?: unknown })._tippy) {
    tip(prevBtn, 'Previous shipment <kbd>&larr;</kbd>');
  }
  if (nextBtn && !(nextBtn as HTMLElement & { _tippy?: unknown })._tippy) {
    tip(nextBtn, 'Next shipment <kbd>&rarr;</kbd>');
  }
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function resolveCoords(waypoint: Record<string, unknown>): { longitude: number; latitude: number } | null {
  const coords = waypoint.coords;
  if (Array.isArray(coords) && coords.length >= 2) {
    const longitude = toFiniteNumber(coords[0]);
    const latitude = toFiniteNumber(coords[1]);
    if (longitude != null && latitude != null) {
      return { longitude, latitude };
    }
  }

  const longitude = toFiniteNumber(waypoint.longitude);
  const latitude = toFiniteNumber(waypoint.latitude);
  if (longitude != null && latitude != null) {
    return { longitude, latitude };
  }

  return null;
}

function resolveTypeValue(rawType: unknown): string {
  const normalized = String(rawType || '').trim().toLowerCase();
  if (!normalized || normalized === 'unknown') return '';
  return typeOptions.some(option => option.value === normalized) ? normalized : '';
}

function resetFormFromState(): void {
  const waypoint = (modalState.waypoint || {}) as Record<string, unknown>;
  form.type = resolveTypeValue(waypoint.type);
  form.name = String(waypoint.name || '');
  form.notes = String(waypoint.notes || '');
  form.googleFormat = false;
  modalState.error = null;

  meta.inspectionRequired = waypoint.inspectionRequired === true;
  meta.verified = waypoint.verified === true;
  meta.inspectionReasons = Array.isArray(waypoint.inspectionReasons)
    ? waypoint.inspectionReasons.map(reason => String(reason))
    : [];
  meta.dateCreated = waypoint.dateCreated ?? null;
  meta.dateModified = waypoint.dateModified ?? null;
  meta.displayCity = String(waypoint.city || '');
  meta.displayCountry = formatCountryDisplay(waypoint.country, waypoint.countryCode);

  const resolvedCoords = resolveCoords(waypoint);
  form.coordinates = resolvedCoords
    ? `${resolvedCoords.longitude}, ${resolvedCoords.latitude}`
    : '';
}

watch(
  () => [modalState.open, modalState.openCount, modalState.waypointId] as const,
  async ([isOpen]) => {
    if (isOpen) {
      resetFormFromState();
      pauseShipmentModalTooltips();
      document.body.classList.add('modal-open');
      await initInfoTooltip();
      return;
    }
    destroyInfoTooltip();
    resumeShipmentModalTooltips();
    document.body.classList.remove('modal-open');
  }
);

watch(infoTooltipContent, content => {
  infoTooltip?.setContent(content);
});

onUnmounted(() => {
  destroyInfoTooltip();
});

function requestClose(): void {
  if (saving.value) return;
  closeEditWaypointModal();
}

function buildPayload(): EditWaypointModalSavePayload | null {
  const name = form.name.trim();
  if (!name) {
    modalState.error = 'Description is required';
    return null;
  }

  const type = form.type.trim();
  if (!type) {
    modalState.error = 'Type is required';
    return null;
  }

  const coordinates = form.coordinates.trim();
  if (!coordinates) {
    modalState.error = 'Coordinates are required';
    return null;
  }

  const parts = coordinates.split(',').map(part => part.trim());
  if (parts.length !== 2) {
    modalState.error = 'Invalid coordinates format. Use: longitude, latitude';
    return null;
  }
  const first = Number(parts[0]);
  const second = Number(parts[1]);
  if (!Number.isFinite(first) || !Number.isFinite(second)) {
    modalState.error = 'Invalid coordinates format. Use: longitude, latitude';
    return null;
  }
  const coords: [number, number] = form.googleFormat ? [second, first] : [first, second];

  return {
    type,
    name,
    notes: form.notes.trim() || undefined,
    coords
  };
}

async function handleSubmit(): Promise<void> {
  const payload = buildPayload();
  if (!payload) return;
  if (typeof modalState.onSave !== 'function') return;

  modalState.saving = true;
  modalState.error = null;
  try {
    await modalState.onSave(payload);
    closeEditWaypointModal();
  } catch (error) {
    modalState.error = error instanceof Error ? error.message : 'Failed to save waypoint.';
  } finally {
    modalState.saving = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (open.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "edit-waypoint-backdrop",
            onMousedown: _withModifiers(requestClose, ["self"])
          }, [
            (_openBlock(), _createElementBlock("form", {
              class: "edit-waypoint-modal",
              key: `edit-waypoint-${_unref(modalState).waypointId || 'none'}-${_unref(modalState).openCount}`,
              onSubmit: _withModifiers(handleSubmit, ["prevent"])
            }, [
              _createElementVNode("header", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    (meta.verified && locationHeaderTitle.value)
                      ? (_openBlock(), _createElementBlock("h2", _hoisted_5, _toDisplayString(locationHeaderTitle.value), 1 /* TEXT */))
                      : (_openBlock(), _createElementBlock("h2", _hoisted_6, "Edit Waypoint")),
                    _createElementVNode("button", {
                      ref_key: "infoTooltipRef",
                      ref: infoTooltipRef,
                      type: "button",
                      class: "edit-waypoint-info",
                      "aria-label": "Waypoint details",
                      tabindex: "-1"
                    }, [...(_cache[5] || (_cache[5] = [
                      _createElementVNode("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        width: "16",
                        height: "16",
                        viewBox: "0 0 24 24",
                        fill: "none",
                        stroke: "currentColor",
                        "stroke-width": "2",
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round",
                        "aria-hidden": "true"
                      }, [
                        _createElementVNode("path", {
                          stroke: "none",
                          d: "M0 0h24v24H0z",
                          fill: "none"
                        }),
                        _createElementVNode("path", { d: "M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" }),
                        _createElementVNode("path", { d: "M12 9h.01" }),
                        _createElementVNode("path", { d: "M11 12h1v4h1" })
                      ], -1 /* CACHED */)
                    ]))], 512 /* NEED_PATCH */)
                  ]),
                  (meta.verified && locationHeaderTitle.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_7, " Edit Waypoint "))
                    : _createCommentVNode("v-if", true)
                ])
              ]),
              _createElementVNode("div", _hoisted_8, [
                (showInspectionAlert.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _createElementVNode("div", _hoisted_10, [
                        _cache[6] || (_cache[6] = _createElementVNode("span", { class: "edit-waypoint-inspection__title" }, "Inspection required", -1 /* CACHED */)),
                        (inspectionReasonLabels.value.length)
                          ? (_openBlock(), _createElementBlock("ul", _hoisted_11, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(inspectionReasonLabels.value, (reason) => {
                                return (_openBlock(), _createElementBlock("li", { key: reason }, _toDisplayString(reason), 1 /* TEXT */))
                              }), 128 /* KEYED_FRAGMENT */))
                            ]))
                          : _createCommentVNode("v-if", true)
                      ])
                    ]))
                  : _createCommentVNode("v-if", true),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("label", _hoisted_13, [
                    _cache[7] || (_cache[7] = _createElementVNode("span", { class: "edit-waypoint-field__label" }, [
                      _createElementVNode("span", {
                        class: "edit-waypoint-field__required",
                        "aria-hidden": "true"
                      }, "*"),
                      _createTextVNode("Description ")
                    ], -1 /* CACHED */)),
                    _createVNode(_unref(Input), {
                      modelValue: form.name,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.name) = $event)),
                      variant: "form",
                      placeholder: "e.g., Toronto Hub",
                      required: "",
                      disabled: saving.value
                    }, null, 8 /* PROPS */, ["modelValue", "disabled"])
                  ]),
                  _createElementVNode("div", _hoisted_14, [
                    _createVNode(_unref(Select), {
                      modelValue: form.type,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.type) = $event)),
                      label: "Type",
                      options: typeOptions,
                      placeholder: "-- select --",
                      variant: "form",
                      "aria-label": "Type",
                      required: "",
                      disabled: saving.value
                    }, null, 8 /* PROPS */, ["modelValue", "disabled"])
                  ])
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _cache[8] || (_cache[8] = _createElementVNode("span", { class: "edit-waypoint-field__label edit-waypoint-coords__label" }, [
                    _createElementVNode("span", {
                      class: "edit-waypoint-field__required",
                      "aria-hidden": "true"
                    }, "*"),
                    _createTextVNode("Coordinates ")
                  ], -1 /* CACHED */)),
                  _createVNode(_unref(Input), {
                    modelValue: form.coordinates,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.coordinates) = $event)),
                    class: "edit-waypoint-coords__input",
                    variant: "form",
                    placeholder: coordinatesPlaceholder.value,
                    required: "",
                    disabled: saving.value
                  }, null, 8 /* PROPS */, ["modelValue", "placeholder", "disabled"]),
                  _createElementVNode("div", _hoisted_16, [
                    _createVNode(_unref(Checkbox), {
                      modelValue: form.googleFormat,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.googleFormat) = $event)),
                      label: "Google",
                      disabled: saving.value
                    }, null, 8 /* PROPS */, ["modelValue", "disabled"])
                  ]),
                  _createElementVNode("small", _hoisted_17, _toDisplayString(coordinatesHelp.value), 1 /* TEXT */)
                ]),
                _createElementVNode("div", _hoisted_18, [
                  _cache[9] || (_cache[9] = _createElementVNode("span", { class: "edit-waypoint-field__label" }, "Notes", -1 /* CACHED */)),
                  _createVNode(_unref(TextArea), {
                    modelValue: form.notes,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.notes) = $event)),
                    variant: "form",
                    rows: 3,
                    "auto-resize": false,
                    placeholder: "Additional details...",
                    disabled: saving.value
                  }, null, 8 /* PROPS */, ["modelValue", "disabled"])
                ]),
                (errorMessage.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_19, _toDisplayString(errorMessage.value), 1 /* TEXT */))
                  : _createCommentVNode("v-if", true)
              ]),
              _createElementVNode("footer", _hoisted_20, [
                _createVNode(_unref(Button), {
                  type: "submit",
                  variant: "solid",
                  tone: "primary",
                  disabled: saving.value
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(saving.value ? 'Saving...' : 'Save'), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["disabled"]),
                _createVNode(_unref(Button), {
                  type: "button",
                  variant: "ghost",
                  tone: "neutral",
                  disabled: saving.value,
                  onClick: requestClose
                }, {
                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                    _createTextVNode(" Cancel ", -1 /* CACHED */)
                  ]))]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["disabled"])
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "edit-waypoint-close",
                "aria-label": "Close",
                onClick: requestClose
              }, [...(_cache[11] || (_cache[11] = [
                _createElementVNode("i", { class: "material-icons" }, "close", -1 /* CACHED */)
              ]))])
            ], 32 /* NEED_HYDRATION */))
          ], 32 /* NEED_HYDRATION */))
        : _createCommentVNode("v-if", true)
    ]))
  ]))
}
}

})